/**
 * MapaSeguimiento.jsx
 * 
 * Componente de mapa para la página de seguimiento del cliente
 * 
 * Qué hace:
 * - Muestra la ubicación del repartidor en tiempo real
 * - Muestra la ubicación del cliente
 * - Dibuja la ruta hacia el cliente
 * - Sigue automáticamente al repartidor
 * 
 * Qué recibe (props):
 * - posicionRepartidor (Object): {lat, lng} de la posición actual del repartidor
 * - puntoCliente (Object): {lat, lng} del punto de entrega del cliente
 * - ruta (Array): Array de coordenadas de la ruta completa
 * - simulando (boolean): Si la simulación está activa
 * 
 * Qué devuelve:
 * - JSX con el componente del mapa
 */

import React, { useEffect, useRef } from 'react'
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'

// Importar iconos de Leaflet
import 'leaflet/dist/leaflet.css'

// Fix para los iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

/**
 * Componente que hace que el mapa siga al repartidor
 */
function SeguirRepartidor({ posicionRepartidor, activo }) {
  const map = useMap()

  useEffect(() => {
    if (!activo || !posicionRepartidor) return

    // Seguir al repartidor con panTo (movimiento suave)
    map.panTo(
      [posicionRepartidor.lat, posicionRepartidor.lng],
      { animate: true, duration: 0.6, easeLinearity: 0.25 }
    )

    // Ajustar zoom para mostrar tanto el repartidor como el cliente
    if (map.getZoom() < 15) {
      map.setZoom(15, { animate: true, duration: 0.5 })
    }
  }, [posicionRepartidor, activo, map])

  return null
}

/**
 * Componente principal del mapa de seguimiento
 */
function MapaSeguimiento({ 
  posicionRepartidor = null,
  puntoCliente = null,
  ruta = [],
  simulando = false
}) {
  // Coordenadas por defecto (Buenos Aires, Argentina) si no hay puntos
  const centroPorDefecto = [-34.6037, -58.3816]
  const zoomPorDefecto = 15

  // Crear icono personalizado para el repartidor
  const iconoRepartidor = L.divIcon({
    className: 'marcador-repartidor',
    html: '<div style="background-color: #ef4444; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); animation: pulse 2s infinite;"></div>',
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  })

  // Crear icono personalizado para el cliente
  const iconoCliente = L.divIcon({
    className: 'marcador-cliente',
    html: '<div style="background-color: #2563eb; width: 35px; height: 35px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: 18px;">🏠</div>',
    iconSize: [35, 35],
    iconAnchor: [17.5, 17.5]
  })

  // Calcular centro del mapa
  let centro = centroPorDefecto
  if (posicionRepartidor) {
    centro = [posicionRepartidor.lat, posicionRepartidor.lng]
  } else if (puntoCliente) {
    centro = [puntoCliente.lat, puntoCliente.lng]
  }

  // Filtrar ruta para mostrar solo la parte relevante (hacia el cliente)
  // Por ahora mostramos toda la ruta, pero podríamos filtrar solo hasta el cliente
  const rutaHaciaCliente = ruta

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={centro}
        zoom={zoomPorDefecto}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Capa de tiles (mapa base) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Seguir al repartidor durante la simulación */}
        {simulando && posicionRepartidor && (
          <SeguirRepartidor 
            posicionRepartidor={posicionRepartidor}
            activo={simulando}
          />
        )}

        {/* Dibujar la ruta hacia el cliente */}
        {rutaHaciaCliente && rutaHaciaCliente.length > 1 && (
          <Polyline
            positions={rutaHaciaCliente}
            pathOptions={{
              color: '#2563eb',
              weight: 5,
              opacity: 0.7
            }}
          />
        )}

        {/* Marcador del cliente */}
        {puntoCliente && (
          <Marker position={[puntoCliente.lat, puntoCliente.lng]} icon={iconoCliente}>
            <Popup>
              <strong>Tu Ubicación</strong>
              <p>El repartidor se dirige hacia aquí</p>
            </Popup>
          </Marker>
        )}

        {/* Marcador del repartidor */}
        {posicionRepartidor && (
          <Marker position={[posicionRepartidor.lat, posicionRepartidor.lng]} icon={iconoRepartidor}>
            <Popup>
              <strong>Repartidor</strong>
              <p>Posición actual del repartidor</p>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Estilos CSS para la animación del pulso */}
      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
      `}</style>
    </div>
  )
}

export default MapaSeguimiento

